import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCalendar,
  lucideCheck,
  lucideChevronLeft,
  lucideChevronRight,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';

export type DateRange = 'ALL' | 'THIS-MONTH' | 'LAST-MONTH' | 'THIS-YEAR' | 'LAST-YEAR' | 'CUSTOM';

export interface DateRangeEvent {
  key: DateRange;
  fromDate?: string;
  toDate?: string;
}

export function dateRangeToParams(range: DateRange): { fromDate?: string; toDate?: string } {
  const today = new Date();
  switch (range) {
    case 'THIS-MONTH':
      return {
        fromDate: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0],
        toDate: today.toISOString().split('T')[0],
      };
    case 'LAST-MONTH':
      return {
        fromDate: new Date(today.getFullYear(), today.getMonth() - 1, 1)
          .toISOString()
          .split('T')[0],
        toDate: new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0],
      };
    case 'THIS-YEAR':
      return {
        fromDate: new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0],
        toDate: today.toISOString().split('T')[0],
      };
    case 'LAST-YEAR':
      return {
        fromDate: new Date(today.getFullYear() - 1, 0, 1).toISOString().split('T')[0],
        toDate: new Date(today.getFullYear() - 1, 11, 31).toISOString().split('T')[0],
      };
    default:
      return {};
  }
}

const MONTHS_DE = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];
const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const PRESET_OPTIONS: { key: DateRange; label: string }[] = [
  { key: 'ALL', label: 'Gesamt' },
  { key: 'THIS-MONTH', label: 'Dieser Monat' },
  { key: 'LAST-MONTH', label: 'Letzter Monat' },
  { key: 'THIS-YEAR', label: 'Dieses Jahr' },
  { key: 'LAST-YEAR', label: 'Letztes Jahr' },
];

/**
 * Date range picker: presets plus a custom two-click range.
 *
 * One trigger on every breakpoint — the popover carries the preset list and,
 * one step deeper, the calendar. The previous version kept a separate mobile
 * bottom sheet and a desktop pill row in parallel.
 */
@Component({
  selector: 'app-date-range-filter',
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    NgIcon,
    HlmButtonImports,
    HlmPopoverImports,
    HlmSeparatorImports,
  ],
  providers: [provideIcons({ lucideCalendar, lucideCheck, lucideChevronLeft, lucideChevronRight })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-popover
      align="end"
      [state]="open ? 'open' : 'closed'"
      (stateChanged)="onPopoverState($event)">
      <button hlmPopoverTrigger hlmBtn variant="outline" (click)="onTriggerClick()">
        <ng-icon name="lucideCalendar" />
        {{ activeChipLabel }}
      </button>

      <hlm-popover-content *hlmPopoverPortal class="w-72 p-1.5" aria-label="Zeitraum wählen">
        @if (!showCalendar) {
          @for (preset of presets; track preset.key) {
            <button
              hlmBtn
              variant="ghost"
              size="sm"
              class="w-full justify-start"
              (click)="selectPreset(preset.key)">
              {{ preset.label }}
              @if (selected === preset.key) {
                <ng-icon name="lucideCheck" class="ms-auto" />
              }
            </button>
          }

          <div hlmSeparator class="my-1"></div>

          <button
            hlmBtn
            variant="ghost"
            size="sm"
            class="w-full justify-start"
            (click)="openCalendar()">
            <ng-icon name="lucideCalendar" />
            {{ customLabel || 'Benutzerdefiniert' }}
            <ng-icon name="lucideChevronRight" class="ms-auto" />
          </button>
        } @else {
          <ng-container *ngTemplateOutlet="calendarTpl"></ng-container>
        }
      </hlm-popover-content>
    </hlm-popover>

    <!-- ── Calendar ── -->
    <ng-template #calendarTpl>
      <div class="p-1.5">
        <div class="mb-2 flex items-center justify-between">
          <button
            hlmBtn
            variant="ghost"
            size="icon-sm"
            aria-label="Vorheriger Monat"
            (click)="prevMonth()">
            <ng-icon name="lucideChevronLeft" />
          </button>
          <span class="text-sm font-medium">{{ monthLabel }}</span>
          <button
            hlmBtn
            variant="ghost"
            size="icon-sm"
            aria-label="Nächster Monat"
            [disabled]="isCurrentMonth"
            (click)="nextMonth()">
            <ng-icon name="lucideChevronRight" />
          </button>
        </div>

        <div class="grid grid-cols-7">
          @for (weekday of weekdays; track weekday) {
            <div class="text-muted-foreground py-1 text-center text-xs font-medium">
              {{ weekday }}
            </div>
          }
        </div>

        <div class="grid grid-cols-7">
          @for (cell of calendarCells; track $index) {
            <div class="relative flex h-9 items-center justify-center">
              @if (cell !== null && isInRange(cell)) {
                <div
                  class="bg-accent absolute inset-y-0.5"
                  [ngClass]="{
                    'right-0 left-1/2': isStart(cell) && !isEnd(cell),
                    'right-1/2 left-0': isEnd(cell) && !isStart(cell),
                    'right-0 left-0': !isStart(cell) && !isEnd(cell),
                  }"></div>
              }
              @if (cell !== null && (isStart(cell) || isEnd(cell))) {
                <div class="bg-primary absolute inset-0 m-auto size-8 rounded-full"></div>
              }
              @if (
                cell !== null &&
                !isStart(cell) &&
                !isEnd(cell) &&
                isHovered(cell) &&
                tempStart &&
                !tempEnd
              ) {
                <div class="ring-ring absolute inset-0 m-auto size-8 rounded-full ring-2"></div>
              }
              @if (cell !== null) {
                <button
                  (click)="clickDay(cell)"
                  (mouseenter)="hoveredDate = dateStr(cell)"
                  (mouseleave)="hoveredDate = null"
                  [disabled]="isFuture(cell)"
                  class="relative z-10 size-8 rounded-full text-sm transition-colors select-none disabled:opacity-25"
                  [ngClass]="dayClass(cell)">
                  {{ cell }}
                </button>
              }
            </div>
          }
        </div>

        <p class="text-muted-foreground mt-2 min-h-4 text-center text-xs">
          @if (!tempStart) {
            Startdatum wählen
          } @else if (!tempEnd) {
            Enddatum wählen
          } @else {
            {{ formatDisplayDate(tempStart) }} – {{ formatDisplayDate(tempEnd) }}
          }
        </p>

        <div hlmSeparator class="my-2"></div>

        <div class="flex items-center justify-end gap-2">
          <button hlmBtn variant="ghost" size="sm" (click)="cancelCalendar()">Abbrechen</button>
          <button hlmBtn size="sm" [disabled]="!tempStart" (click)="apply()">Anwenden</button>
        </div>
      </div>
    </ng-template>
  `,
})
export class DateRangeFilterComponent implements OnChanges {
  @Input() selected: DateRange = 'ALL';
  @Output() changed = new EventEmitter<DateRangeEvent>();

  readonly presets = PRESET_OPTIONS;
  readonly weekdays = WEEKDAYS_DE;

  open = false;
  /** Second level of the popover: the custom range calendar. */
  showCalendar = false;

  today = new Date().toISOString().split('T')[0];

  // Calendar navigation
  calYear = new Date().getFullYear();
  calMonth = new Date().getMonth();

  // Selection state
  tempStart: string | null = null;
  tempEnd: string | null = null;
  hoveredDate: string | null = null;

  // Applied custom range
  activeFrom = '';
  activeTo = '';

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selected'] && this.selected !== 'CUSTOM') {
      this.activeFrom = '';
      this.activeTo = '';
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.close();
  }

  // ── Popover ──────────────────────────────────────────

  onTriggerClick() {
    // Always start on the preset list, never on the calendar of a previous visit.
    this.showCalendar = false;
    this.initCalendar();
    this.open = !this.open;
  }

  onPopoverState(state: 'open' | 'closed') {
    this.open = state === 'open';
    if (!this.open) {
      this.showCalendar = false;
      this.hoveredDate = null;
    }
  }

  private close() {
    this.open = false;
    this.showCalendar = false;
    this.hoveredDate = null;
  }

  openCalendar() {
    this.initCalendar();
    this.showCalendar = true;
  }

  // ── Selection ────────────────────────────────────────

  selectPreset(key: DateRange) {
    this.selected = key;
    this.activeFrom = '';
    this.activeTo = '';
    this.close();
    this.changed.emit({ key, ...dateRangeToParams(key) });
  }

  cancelCalendar() {
    if (this.showCalendar) {
      this.showCalendar = false;
    } else {
      this.close();
    }
  }

  private initCalendar() {
    this.tempStart = this.activeFrom || null;
    this.tempEnd = this.activeTo || null;
    const ref = this.activeFrom ? new Date(this.activeFrom) : new Date();
    this.calYear = ref.getFullYear();
    this.calMonth = ref.getMonth();
  }

  // ── Month navigation ─────────────────────────────────

  get monthLabel(): string {
    return `${MONTHS_DE[this.calMonth]} ${this.calYear}`;
  }

  get isCurrentMonth(): boolean {
    const now = new Date();
    return this.calYear === now.getFullYear() && this.calMonth === now.getMonth();
  }

  prevMonth() {
    if (this.calMonth === 0) {
      this.calMonth = 11;
      this.calYear--;
    } else this.calMonth--;
  }

  nextMonth() {
    if (this.isCurrentMonth) return;
    if (this.calMonth === 11) {
      this.calMonth = 0;
      this.calYear++;
    } else this.calMonth++;
  }

  // ── Calendar grid ────────────────────────────────────

  get calendarCells(): (number | null)[] {
    const daysInMonth = new Date(this.calYear, this.calMonth + 1, 0).getDate();
    const firstDow = new Date(this.calYear, this.calMonth, 1).getDay();
    const offset = (firstDow + 6) % 7; // Monday-first
    const cells: (number | null)[] = Array(offset).fill(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }

  dateStr(day: number): string {
    return `${this.calYear}-${String(this.calMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  isFuture(day: number): boolean {
    return this.dateStr(day) > this.today;
  }
  isToday(day: number): boolean {
    return this.dateStr(day) === this.today;
  }
  isStart(day: number): boolean {
    return !!this.tempStart && this.dateStr(day) === this.tempStart;
  }
  isEnd(day: number): boolean {
    return !!this.tempEnd && this.dateStr(day) === this.tempEnd;
  }
  isHovered(day: number): boolean {
    return !!this.hoveredDate && this.dateStr(day) === this.hoveredDate;
  }

  isInRange(day: number): boolean {
    const d = this.dateStr(day);
    const start = this.tempStart;
    const end = this.tempEnd ?? (this.hoveredDate && this.tempStart ? this.hoveredDate : null);
    if (!start || !end) return false;
    const [s, e] = start <= end ? [start, end] : [end, start];
    return d >= s && d <= e;
  }

  dayClass(day: number): string {
    if (this.isStart(day) || this.isEnd(day)) return 'text-primary-foreground font-medium';
    if (this.isInRange(day)) return 'text-accent-foreground';
    if (this.isToday(day)) return 'hover:bg-accent font-semibold';
    return 'hover:bg-accent';
  }

  clickDay(day: number) {
    if (this.isFuture(day)) return;
    const d = this.dateStr(day);
    if (!this.tempStart || (this.tempStart && this.tempEnd)) {
      this.tempStart = d;
      this.tempEnd = null;
    } else {
      if (d < this.tempStart) {
        this.tempEnd = this.tempStart;
        this.tempStart = d;
      } else this.tempEnd = d;
    }
    this.hoveredDate = null;
  }

  // ── Apply ────────────────────────────────────────────

  apply() {
    if (!this.tempStart) return;
    this.selected = 'CUSTOM';
    this.activeFrom = this.tempStart;
    this.activeTo = this.tempEnd ?? '';
    this.close();
    this.changed.emit({
      key: 'CUSTOM',
      fromDate: this.tempStart,
      toDate: this.tempEnd ?? undefined,
    });
  }

  // ── Labels ───────────────────────────────────────────

  get customLabel(): string {
    if (this.selected !== 'CUSTOM' || !this.activeFrom) return '';
    const from = this.formatDisplayDate(this.activeFrom);
    const to = this.activeTo ? this.formatDisplayDate(this.activeTo) : '…';
    return `${from} – ${to}`;
  }

  get activeChipLabel(): string {
    if (this.selected === 'CUSTOM') return this.customLabel || 'Benutzerdefiniert';
    return PRESET_OPTIONS.find((p) => p.key === this.selected)?.label ?? 'Gesamt';
  }

  formatDisplayDate(iso: string): string {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  }
}
